import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Search, Clock, ChevronDown, ChevronRight,
  CheckCircle, Lightbulb, AlertTriangle,
  Briefcase, Phone, CreditCard, Bell,
  MessageSquare, Zap, FileText,
} from "lucide-react";

import "../styles/marketing.css";
import Seo, { breadcrumbJsonLd } from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";

const tutorials = [
  {
    category: "Getting Started",
    icon: Zap,
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    guides: [
      {
        title: "Complete Platform Overview",
        readTime: "5 min read",
        description: "A full walkthrough of the Fieldz AI dashboard, key features, and navigation.",
        steps: [
          { label: "Sign in to your account", detail: "Go to your Fieldz AI URL and log in with your email and password. If you're new, click 'Get Started' to create an account with a 7-day free trial." },
          { label: "Explore the Dashboard", detail: "After login, you land on the Dashboard Overview. This is your command center showing today's scheduled jobs, revenue summary, active technicians, and recent activity feed." },
          { label: "Navigate the Sidebar", detail: "The left sidebar contains all major sections: Jobs, Customers, Messages, Invoices, Team, Financials, Analytics, Automations, Inventory, and Settings. Click any item to navigate." },
          { label: "Understanding the Top Bar", detail: "The top bar shows your company name, notification bell (for push alerts), and your profile menu. From the profile menu you can access account settings, switch themes (light/dark), and log out." },
          { label: "Quick Actions", detail: "Use the '+New Job' button available on the Jobs page to quickly create jobs. The dialer FAB (floating action button) on the dashboard lets you make calls directly from the browser." },
        ],
        tips: [
          "Bookmark your dashboard URL for quick access",
          "Use keyboard shortcuts: press '/' to focus search fields on most pages",
        ],
      },
      {
        title: "Setting Up Your Business Profile",
        readTime: "3 min read",
        description: "Configure your company name, logo, timezone, service area, and business settings.",
        steps: [
          { label: "Navigate to Settings", detail: "Click 'Settings' in the left sidebar. This opens the Settings page with multiple tabs: Business, Phones, Templates, Services, Payment Types, Job Sources, and more." },
          { label: "Fill in Business Information", detail: "Under the 'Business' tab, enter your company name, business address, phone number, and email. This information appears on invoices, emails, and customer communications." },
          { label: "Upload Your Logo", detail: "Click the logo upload area to add your company logo. Supported formats: PNG, JPG, SVG. The logo appears on invoices, the customer portal, and email communications." },
          { label: "Set Your Timezone", detail: "Select your timezone from the dropdown. This ensures all scheduled jobs, reminders, and reports use the correct local time for your business." },
          { label: "Configure Services & Sources", detail: "Under the 'Services' tab, add the types of services you offer (e.g., Locksmith, Garage Door, Carpet Cleaning). Under 'Job Sources', add where your leads come from (e.g., Google, Yelp, Referral)." },
        ],
        tips: [
          "Set up your Payment Types early — these are used when completing jobs to track how customers pay (Cash, Card, Check, etc.)",
          "Your business profile information is what customers see, so keep it professional and accurate",
        ],
      },
      {
        title: "Adding Your First Team Member",
        readTime: "4 min read",
        description: "Learn how to invite technicians, set commission rates, and manage team roles.",
        steps: [
          { label: "Go to the Team page", detail: "Click 'Team' in the sidebar. This shows all your team members including their role, status, and contact information." },
          { label: "Click 'Add Member'", detail: "Click the 'Add Member' button in the top-right corner. A form will appear to enter the new member's details." },
          { label: "Fill in Member Details", detail: "Enter their name, email address, and phone number. Select their role: 'Admin' (full access), 'Dispatcher' (job management), or 'Technician' (field work)." },
          { label: "Configure Commission (Technicians)", detail: "For technicians, set their default commission rate (percentage of job revenue). You can also configure per-source and per-service commission overrides for more granular control." },
          { label: "Send the Invite", detail: "Save the member. Technicians automatically receive an SMS invite with a setup link. They'll create their own password and access their Technician Portal at /tech/login." },
        ],
        tips: [
          "Technicians can only see their own assigned jobs and earnings — they can't access other technicians' data or admin features",
          "You can set different commission rates per job source (e.g., higher commission for self-generated leads vs. company leads)",
        ],
        warning: "Make sure the phone number is correct — the SMS invite is sent to this number and cannot be re-sent until the member is re-created.",
      },
    ],
  },
  {
    category: "Jobs & Scheduling",
    icon: Briefcase,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    guides: [
      {
        title: "Creating and Managing Jobs",
        readTime: "6 min read",
        description: "Step-by-step guide to creating jobs, assigning technicians, setting schedules, and tracking progress.",
        steps: [
          { label: "Open the Jobs page", detail: "Click 'Jobs' in the sidebar. You'll see your jobs in either List or Calendar view. Use the toggle at the top to switch between views." },
          { label: "Click '+New Job'", detail: "Click the '+New Job' button. The job creation dialog opens with fields for customer info, service details, scheduling, and pricing." },
          { label: "Add Customer Information", detail: "Either select an existing customer from the dropdown, or enter new customer details (name, phone, email, address). New customers are automatically created in your database." },
          { label: "Set Service & Scheduling", detail: "Choose the service type, set the scheduled date and time, and estimated duration. Add any notes for the technician about the job." },
          { label: "Assign a Technician", detail: "Select a technician from the dropdown. They'll be notified via SMS (if automations are enabled) about the new assignment with job details and address." },
          { label: "Set Pricing", detail: "Enter the estimated price. You can add line items, parts, and labor charges. Processing fees are calculated based on the payment type selected at completion." },
          { label: "Save the Job", detail: "Click 'Create Job'. The job appears on both the List and Calendar views. The assigned technician sees it in their portal immediately." },
        ],
        tips: [
          "Use the 'Paste Ticket' feature to quickly parse job details from a text ticket — it auto-fills customer name, phone, address, and service type",
          "Jobs can be filtered by status (Scheduled, In Progress, Completed, Cancelled), date, and assigned technician",
        ],
      },
      {
        title: "Using the Calendar View",
        readTime: "3 min read",
        description: "Navigate the calendar view, filter by technician, and manage your schedule visually.",
        steps: [
          { label: "Switch to Calendar View", detail: "On the Jobs page, click the Calendar icon toggle in the header to switch from List to Calendar view." },
          { label: "Navigate Between Months", detail: "Use the left/right arrows to navigate between months. Click 'Today' to jump back to the current month." },
          { label: "View Job Details", detail: "Each day cell shows job cards with the service icon, customer name, time, and status. Click a job card to open its full details." },
          { label: "Filter by Technician", detail: "Use the technician dropdown filter in the header to show only jobs assigned to a specific technician or view unassigned jobs." },
          { label: "Filter by Status", detail: "Use the status filter badges (All, Scheduled, In Progress, Completed) to show only jobs with a specific status." },
        ],
        tips: [
          "The calendar is ideal for daily/weekly scheduling — use List view for searching across longer date ranges",
          "Job cards are color-coded by status: blue for scheduled, amber for in-progress, green for completed",
        ],
      },
      {
        title: "Job Pricing & Parts",
        readTime: "5 min read",
        description: "Add line items, parts, and labor charges. Set up payment types and track costs.",
        steps: [
          { label: "Open Job Details", detail: "Click on any job to open its detail dialog. Navigate to the pricing section to see the current pricing breakdown." },
          { label: "Add Parts", detail: "In the Parts section, click 'Add Part'. Enter the part name, cost, and select the provider: 'Company' (you supply it) or 'Technician' (tech supplies it, gets reimbursed)." },
          { label: "Set Total Charged", detail: "Enter the total amount charged to the customer. This is the gross revenue before any deductions." },
          { label: "Record Payment", detail: "When completing a job, select the payment type(s) — Cash, Credit Card, Check, Zelle, etc. You can split payments across multiple types." },
          { label: "Review Financial Breakdown", detail: "After completion, the job shows a full breakdown: gross revenue, technician commission, company parts cost, technician parts (reimbursed), processing fees, and net profit." },
        ],
        tips: [
          "Parts marked as 'Technician' provider are added back to the technician's earnings as reimbursement",
          "Processing fees are automatically calculated based on the fee percentage configured for each payment type in Settings",
        ],
      },
    ],
  },
  {
    category: "Phone System",
    icon: Phone,
    color: "text-purple-500",
    bg: "bg-purple-500/10",
    guides: [
      {
        title: "Buying & Configuring Phone Numbers",
        readTime: "5 min read",
        description: "Purchase virtual numbers, set up call forwarding, IVR menus, and SMS routing.",
        steps: [
          { label: "Go to Settings > Phones", detail: "Click 'Settings' in the sidebar, then click the 'Phones' tab. You'll see your current phone numbers and a 'Buy Number' button." },
          { label: "Search for a Number", detail: "Click 'Buy Number'. Enter the area code you want (e.g., 916 for Sacramento). Available numbers appear with their capabilities (Voice, SMS)." },
          { label: "Purchase the Number", detail: "Click 'Buy' next to your preferred number. The number is provisioned instantly through Twilio and appears in your phone list." },
          { label: "Configure Call Flow", detail: "Click the settings gear icon on any number. You can set up: Simple Forwarding (ring one number), Round Robin (distribute across team), IVR Menu (press 1 for...), or Time-Based routing." },
          { label: "Test Your Number", detail: "Call your new number from any phone to verify the call flow works correctly. Check the Call Logs page to see the call recorded." },
        ],
        tips: [
          "You can own multiple numbers — useful for different service lines or marketing campaigns",
          "IVR menus can include whisper messages that play to the agent before connecting, telling them which line the customer called",
        ],
      },
      {
        title: "Call Masking Setup",
        readTime: "4 min read",
        description: "Enable call masking so technicians can call customers while keeping numbers private.",
        steps: [
          { label: "Enable Call Masking", detail: "Go to Settings > Call Masking tab. Toggle 'Enable Call Masking' on. Select which phone number to use as the masked caller ID." },
          { label: "Set a Fallback Number", detail: "Enter a fallback phone number. When customers call back the masked number and no active job/technician is found, calls route to this number (typically your office)." },
          { label: "How It Works (Outbound)", detail: "When a job is assigned, a unique 4-digit PIN is generated. The technician calls the masked number, enters their PIN, and is connected to the customer. The customer sees your business number, not the tech's personal number." },
          { label: "How It Works (Inbound)", detail: "When a customer calls back the masked number, the system identifies them by caller ID, looks up their active job, and automatically routes the call to the assigned technician — no PIN needed." },
          { label: "View Masked Call Logs", detail: "All masked calls (inbound and outbound) are logged in the Call Logs page with recordings. They also appear in the job's activity history." },
        ],
        tips: [
          "PINs are automatically deactivated when a job is completed or cancelled",
          "If a technician has multiple active jobs, they select which customer to call via the PIN system",
        ],
        warning: "Call masking requires at least one purchased phone number. The masked number should not also be used as your main business line to avoid routing conflicts.",
      },
      {
        title: "Managing SMS Conversations",
        readTime: "3 min read",
        description: "Send, receive, and organize SMS messages with customers from your dashboard.",
        steps: [
          { label: "Open the Messages page", detail: "Click 'Messages' in the sidebar. You'll see a list of all SMS conversations, organized by customer phone number with the most recent at the top." },
          { label: "Start a New Conversation", detail: "Click 'New Message' and enter the customer's phone number. Select which of your phone numbers to send from." },
          { label: "Send a Message", detail: "Type your message in the input field and click Send. You can also use SMS templates by clicking the template icon — templates auto-fill with customer and job data." },
          { label: "View Conversation History", detail: "Click any conversation to see the full message thread. Inbound messages (from customers) appear on the left, outbound (from you) on the right." },
          { label: "Push Notifications", detail: "When a customer replies, you'll receive a push notification (if enabled) on both web and mobile. The Messages page shows unread indicators." },
        ],
        tips: [
          "SMS templates with placeholders like {customer_name} and {job_date} save time and ensure consistency",
          "All SMS messages count toward your plan's monthly SMS limit — check Settings > My Plan for current usage",
        ],
      },
    ],
  },
  {
    category: "Billing & Invoicing",
    icon: CreditCard,
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    guides: [
      {
        title: "Creating Invoices & Estimates",
        readTime: "5 min read",
        description: "Generate professional invoices and estimates, add line items, and send to customers.",
        steps: [
          { label: "Go to the Invoices page", detail: "Click 'Invoices' in the sidebar. You'll see all invoices organized by status: Draft, Sent, Paid, and Overdue." },
          { label: "Create a New Invoice", detail: "Click '+New Invoice'. Select a customer (or create one inline), then add line items with description, quantity, and unit price." },
          { label: "Configure Tax & Totals", detail: "Set the tax rate if applicable. The subtotal, tax amount, and grand total calculate automatically. Add a due date and any notes." },
          { label: "Send to Customer", detail: "Click 'Send' to email the invoice to the customer. They receive a professional email with the invoice details. You can also share via SMS." },
          { label: "Create an Estimate Instead", detail: "Toggle 'Estimate' mode when creating. Estimates work identically to invoices but are labeled as estimates. When approved, convert to invoice with one click." },
        ],
        tips: [
          "Invoices can be auto-generated from completed jobs — the line items and pricing carry over automatically",
          "Customers can view and pay invoices through the Customer Portal if you have Stripe connected",
        ],
      },
      {
        title: "Financial Reports & Analytics",
        readTime: "6 min read",
        description: "Navigate the financials dashboard, view technician reports, and export data.",
        steps: [
          { label: "Open the Financials page", detail: "Click 'Financials' in the sidebar. The page shows revenue, expenses, commissions, and profit for the selected date range." },
          { label: "Set Date Range", detail: "Use the date picker at the top to select a custom range. Quick options: Today, This Week, This Month, Last Month, Custom Range." },
          { label: "View Revenue Breakdown", detail: "The main cards show total revenue, total commission paid to technicians, processing fees, parts costs, and your net profit." },
          { label: "Technician Breakdown", detail: "Scroll down to see per-technician performance: jobs completed, revenue generated, commission earned, parts reimbursed, and balance owed." },
          { label: "Export Reports", detail: "Click the 'Export' button to download the report as CSV. This includes all jobs, financial breakdowns, and technician summaries for the selected period." },
        ],
        tips: [
          "The 'Balance' column shows what each technician owes the company (cash collected minus their net earnings) — use Settlements to reconcile",
          "Compare months using the date range selector to track business growth trends",
        ],
      },
      {
        title: "Technician Settlements",
        readTime: "3 min read",
        description: "Track technician balances, create settlements, and manage payouts.",
        steps: [
          { label: "Understand the Balance", detail: "When technicians collect cash payments, they owe the company the difference between what they collected and their net earnings (commission + reimbursements). This is the 'balance' or 'amount owed'." },
          { label: "View Technician Balances", detail: "On the Financials page, each technician row shows their current balance. A positive balance means they owe the company money." },
          { label: "Create a Settlement", detail: "Click 'Settle' next to a technician. Enter the amount being settled (e.g., cash being turned in). Add notes describing the settlement." },
          { label: "Track Settlement History", detail: "All settlements are recorded with timestamps, amounts, and notes. View the history to see past settlements and running balances." },
        ],
        tips: [
          "Create settlements regularly (daily or weekly) to keep balances manageable",
          "Adjustments can be added for tips, bonuses, or deductions outside normal job flow",
        ],
      },
    ],
  },
  {
    category: "Automations & Notifications",
    icon: Bell,
    color: "text-rose-500",
    bg: "bg-rose-500/10",
    guides: [
      {
        title: "Setting Up Automations",
        readTime: "5 min read",
        description: "Configure automatic SMS reminders, job notifications, and review requests.",
        steps: [
          { label: "Go to Automations", detail: "Click 'Automations' in the sidebar (under 'Fieldz AI' section). You'll see available automation types organized by trigger event." },
          { label: "Enable Job Assignment Notifications", detail: "Toggle on the 'Job Assigned' automation. When a job is assigned to a technician, they automatically receive an SMS with the customer name, address, date, time, and an accept/decline link." },
          { label: "Set Up Appointment Reminders", detail: "Enable 'Appointment Reminder' automation. Configure the timing (e.g., 24 hours before, 4 hours before). Customers receive an SMS reminder about their upcoming appointment." },
          { label: "Configure Review Requests", detail: "Enable 'Review Request' automation. After a job is completed, the customer receives an SMS asking them to leave a review. Set the delay (e.g., 2 hours after completion)." },
          { label: "Customize Templates", detail: "Each automation uses an SMS template. Click 'Edit Template' to customize the message. Use placeholders like {customer_name}, {job_date}, {technician_name} for personalization." },
        ],
        tips: [
          "Start with just job assignment and appointment reminder automations — these have the highest impact on operations",
          "Review requests sent 1-2 hours after completion get the best response rates",
        ],
      },
      {
        title: "Customizing SMS Templates",
        readTime: "3 min read",
        description: "Edit templates with dynamic placeholders for personalized customer communication.",
        steps: [
          { label: "Go to Settings > Templates", detail: "Navigate to Settings and click the 'Templates' tab. You'll see all your SMS templates organized by trigger event." },
          { label: "Edit a Template", detail: "Click on any template to edit it. The editor shows the current message text with highlighted placeholders." },
          { label: "Use Placeholders", detail: "Available placeholders include: {customer_name}, {business_name}, {job_date}, {job_time}, {job_address}, {job_type}, {technician_name}, {assignment_link}, and more." },
          { label: "Preview the Message", detail: "The preview section shows how the message will look with sample data filled in. Check the character count — SMS messages over 160 characters are sent as multiple segments." },
          { label: "Save and Test", detail: "Save your changes. Create a test job to verify the automated message sends correctly with all placeholders properly filled." },
        ],
        tips: [
          "Keep messages concise — under 160 characters when possible to avoid multi-segment SMS charges",
          "Always include {business_name} so customers know who the message is from",
        ],
      },
      {
        title: "Push Notification Setup",
        readTime: "3 min read",
        description: "Enable push notifications for web and mobile to never miss important updates.",
        steps: [
          { label: "Allow Browser Notifications", detail: "When you first log in, your browser will ask for notification permission. Click 'Allow' to enable web push notifications via OneSignal." },
          { label: "Mobile App Notifications", detail: "If you use the Fieldz AI mobile app, push notifications are automatically registered when you log in. Ensure notifications are enabled in your phone's Settings > Apps > Fieldz AI." },
          { label: "What Triggers Notifications", detail: "You'll receive push notifications for: new job assignments, job status changes, incoming SMS messages, appointment reminders, and system alerts." },
          { label: "Test Notifications", detail: "Go to Settings > Notifications and click 'Send Test Notification'. You should receive a push notification on all your registered devices within a few seconds." },
        ],
        tips: [
          "Push notifications work on both desktop browsers (Chrome, Firefox, Edge) and the mobile app simultaneously",
          "If you're not receiving notifications, check your browser's notification settings — they may have been blocked",
        ],
        warning: "Push notifications require HTTPS and a modern browser. Safari on iOS has limited support — use the native app for the best mobile experience.",
      },
    ],
  },
];

const GuideCard = ({ guide, isOpen, onToggle }) => {
  return (
    <div className={`rounded-2xl border bg-white transition-all ${isOpen ? "border-blue-200 shadow-[0_10px_28px_-10px_rgba(0,70,190,0.15)]" : "border-slate-200 hover:border-blue-200"}`}>
      <button
        className="w-full text-left p-5 flex items-start gap-4 cursor-pointer"
        onClick={onToggle}
        data-testid={`guide-toggle-${guide.title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-slate-900 mb-1">{guide.title}</h3>
          <p className="text-slate-500 text-xs leading-relaxed">{guide.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 mt-0.5">
          <span className="inline-flex items-center text-[10px] font-semibold text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
            <Clock className="w-3 h-3 mr-1" />{guide.readTime}
          </span>
          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {isOpen && (
        <div className="px-5 pb-5 pt-4 border-t border-slate-100">
          {/* Steps */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Step-by-step walkthrough</p>
            {guide.steps.map((step, i) => (
              <div key={i} className="flex gap-3" data-testid={`guide-step-${i}`}>
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-7 h-7 rounded-full bg-[var(--mk-blue-light)] flex items-center justify-center text-xs font-bold text-[var(--mk-blue)]">{i + 1}</div>
                  {i < guide.steps.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                </div>
                <div className="pb-3 flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900">{step.label}</p>
                  <p className="text-slate-500 text-xs leading-relaxed mt-0.5">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tips */}
          {guide.tips && guide.tips.length > 0 && (
            <div className="mt-4 bg-emerald-50/70 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-semibold text-emerald-700">Pro Tips</p>
              </div>
              <ul className="space-y-1.5">
                {guide.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warning */}
          {guide.warning && (
            <div className="mt-3 bg-amber-50/70 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-600">{guide.warning}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const VideoTutorialsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openGuides, setOpenGuides] = useState({});

  const toggleGuide = (key) => {
    setOpenGuides((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filtered = searchQuery
    ? tutorials.map((cat) => ({
        ...cat,
        guides: cat.guides.filter(
          (g) =>
            g.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            g.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            g.steps.some((s) => s.label.toLowerCase().includes(searchQuery.toLowerCase()) || s.detail.toLowerCase().includes(searchQuery.toLowerCase()))
        ),
      })).filter((cat) => cat.guides.length > 0)
    : tutorials;

  const totalGuides = tutorials.reduce((sum, cat) => sum + cat.guides.length, 0);

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="tutorials-page-root">
      <Seo
        title="Tutorials — Step-by-Step Fieldz AI Guides"
        description="Detailed walkthroughs for every Fieldz AI feature: jobs and scheduling, phone system and call masking, invoicing, financial reports, and automations."
        path="/tutorials"
        jsonLd={breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Tutorials", path: "/tutorials" }])}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 bg-gradient-to-b from-blue-50/70 via-white to-white px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5 flex items-center justify-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5" /> Tutorials
        </p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-4">Step-by-Step Guides</h1>
        <p className="text-lg text-slate-600 max-w-xl mx-auto mb-2">Detailed walkthroughs to help you master every feature of Fieldz AI</p>
        <p className="text-sm text-slate-500">{totalGuides} guides across {tutorials.length} categories</p>
        <div className="relative max-w-xl mx-auto mt-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            placeholder="Search guides, steps, tips..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mk-input"
            style={{ padding: "16px 18px 16px 48px", borderRadius: 14 }}
            data-testid="tutorials-search"
          />
        </div>
      </section>

      {/* Guides */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-10">
          {filtered.map((cat, ci) => (
            <div key={ci}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-9 h-9 rounded-lg ${cat.bg} flex items-center justify-center`}>
                  <cat.icon className={`w-4.5 h-4.5 ${cat.color}`} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{cat.category}</h2>
                  <p className="text-xs text-slate-500">{cat.guides.length} guide{cat.guides.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <div className="space-y-3">
                {cat.guides.map((guide, gi) => {
                  const key = `${ci}-${gi}`;
                  return (
                    <GuideCard
                      key={key}
                      guide={guide}
                      isOpen={!!openGuides[key]}
                      onToggle={() => toggleGuide(key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No guides found for "{searchQuery}"</p>
              <p className="text-slate-400 text-xs mt-1">Try a different search term</p>
            </div>
          )}

          {/* CTA */}
          <div className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/70 to-white p-7 text-center shadow-[0_10px_30px_-8px_rgba(0,70,190,0.12)]">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Need more help?</h3>
            <p className="text-slate-600 text-sm mb-5">Can't find what you're looking for? Check the full documentation or reach out to our support team.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/docs" className="mk-btn mk-btn-outline"><FileText className="w-4 h-4" /> Full Documentation</Link>
              <Link to="/contact" className="mk-btn mk-btn-primary"><MessageSquare className="w-4 h-4" /> Contact Support</Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default VideoTutorialsPage;
